import { UserMenu } from '@/features/auth/components/UserMenu';
import { ProfilePage } from '@/features/profile/components/ProfilePage';
import { DashboardPage } from '@/features/dashboard/components/DashboardPage';
import { EditorPage } from '@/features/funnel-editor/components/EditorPage';
import { TemplateGalleryPage } from '@/features/templates/components/TemplateGalleryPage';
import { PresentationPage } from '@/features/presentation/PresentationPage';
import { TrashPage } from '@/features/trash/TrashPage';
import { DocumentationPage } from '@/features/documentation/DocumentationPage';
import { AdminPage } from '@/features/admin/AdminPage';
import { useAuthStore } from '@/features/auth/state/auth-store';
import { navigate } from './router';

const FUNNEL_ROUTE_PATTERN = /^\/app\/funnels\/([^/]+)$/;
const PRESENTATION_ROUTE_PATTERN = /^\/app\/funnels\/([^/]+)\/present$/;
const DOCUMENTATION_ROUTE_PATTERN = /^\/app\/funnels\/([^/]+)\/documentation$/;

/**
 * Authenticated shell (SPEC-002 §15, replaced by SPEC-003 T036: `/app`
 * now renders the real dashboard instead of the placeholder).
 */
export function AuthenticatedShell({ path }: { path: string }) {
  const isAdmin = useAuthStore((state) => state.user?.isAdmin === true);
  const presentationMatch = path.match(PRESENTATION_ROUTE_PATTERN);
  const documentationMatch = path.match(DOCUMENTATION_ROUTE_PATTERN);
  const funnelMatch =
    presentationMatch || documentationMatch ? null : path.match(FUNNEL_ROUTE_PATTERN);

  // PRES-PRINCIPLE-004 — the presentation route renders its own minimal
  // header (title/scenario/focus/exit) and must not carry the app chrome
  // (dashboard nav, user menu) into the "clean, controlled" view.
  if (presentationMatch) {
    return <PresentationPage id={presentationMatch[1]!} />;
  }

  return (
    <div className="min-h-screen bg-bg-app">
      <header className="flex h-14 items-center justify-between border-b border-border-subtle px-6">
        <button
          type="button"
          onClick={() => navigate('/app')}
          className="text-sm font-semibold tracking-tight text-text-primary"
        >
          Dark Funnels
        </button>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <button
              type="button"
              onClick={() => navigate('/app/admin')}
              className="text-sm text-text-secondary hover:text-accent"
            >
              Administração
            </button>
          )}
          <UserMenu />
        </div>
      </header>

      {path === '/app/profile' ? (
        <ProfilePage />
      ) : path === '/app/trash' ? (
        <TrashPage />
      ) : path === '/app/templates' ? (
        <TemplateGalleryPage />
      ) : path === '/app/admin' && isAdmin ? (
        <AdminPage />
      ) : path === '/app/admin' ? (
        <main className="mx-auto max-w-3xl px-6 py-16">
          <h1 className="text-2xl font-semibold text-text-primary">
            Acesso de administrador necessário
          </h1>
          <p className="mt-2 text-text-muted">
            Esta rota operacional é restrita pela política de administração no servidor.
          </p>
        </main>
      ) : documentationMatch ? (
        <DocumentationPage id={documentationMatch[1]!} />
      ) : funnelMatch ? (
        <EditorPage id={funnelMatch[1]!} />
      ) : (
        <DashboardPage />
      )}
    </div>
  );
}
